// One opportunity's own page, /deals/:id (pipeline P5 step 7; DEALS-LIVE-CARD-BRIEF-2026-10-04.md section 5 item 9,
// drawn in b-deal-page.html; U239, Mike's "Its own page").
//
// The page head; at the top the card's own parts (the stage, Latest, Cobrain's change and the step, then keenness, the
// year's figure, the product and who holds it), from the same read as Deals, so an edit here is the card's edit. Then
// "Everything on this deal", newest first: every message filed under it, every change Cobrain made (with Undo while its
// week lasts) and every edit a person made, with who and when. "Also at <account>" sits beside it from 1100 wide and
// below it under that. Search results and triage's deal panel open this page (find/search.ts, ItemPage.tsx).
//
// A deal that is no longer open is not on the heat read: its page is the head and its history alone.
import { ActionButton, CompanyLogo as Logo } from '../parts/index.js';
import { useState } from 'react';
import { Loading, useLoad, type PageProps } from '../records/shared.js';
import { dealsApi, DealsError, type DealAct, type DealHead, type DealHistory, type Heat, type HeatRow, type HistoryChange, type HistoryEvent } from './api.js';
import { DealTop, KeenBlock, liveContextOf, Status, Track, useCardNotes, type LiveContext } from './LiveCard.js';
import { actUndoable, actUndoWords, actWords, changeWords, dayWords, historySender, historyUndoable, holderWords, kindChip, opensPath, rowStep, stageTone, subjectWords } from './live-words.js';
import { headWords } from './DealPage.js';
import { possessive } from './heat-words.js';
import './deals.css';
import { SameDeal } from '../dealwork/SameDeal.js';
import { Origins } from '../dealwork/Origins.js';
import { DealCloseState } from './ClosedDeals.js';
import { closeApi } from './close-api.js';
import { CloseToasts } from './CloseBox.js';

/** The newest shown before "Show <n> older". */
export const HISTORY_FIRST = 10;

/** The opportunity in the heat read, and the other opportunities at its account, keenest first. A parked one is a line of
 *  the read like any other since pipeline FT1. */
export function rowAndAlso(heat: Heat, dealId: string): { row: HeatRow | null; also: HeatRow[] } {
  const row = heat.lines.find((r) => r.id === dealId);
  if (!row) return { row: null, also: [] };
  return { row, also: heat.lines.filter((r) => r.accountId === row.accountId && r.id !== dealId).sort((a, b) => (b.keenness ?? -1) - (a.keenness ?? -1)) };
}

export function DealHistoryPage({ store, open, id, head }: PageProps & { id: string; head: DealHead }) {
  const tenant = store.tenantId;
  const [histLoad, reloadHist] = useLoad(() => dealsApi.history(tenant, id), [tenant, id]);
  const [heatLoad] = useLoad(() => dealsApi.heat(tenant), [tenant]);
  // After a write the page is read again quietly, both halves, as Deals is: a reload would blank it to Loading.
  const [fresh, setFresh] = useState<{ heat: Heat | null; hist: DealHistory | null }>({ heat: null, hist: null });
  const kept = useCardNotes();
  const [all, setAll] = useState(false);
  const refresh = () => {
    void dealsApi.heat(tenant).then((h) => setFresh((f) => ({ ...f, heat: h }))).catch(() => undefined);
    void dealsApi.history(tenant, id).then((h) => setFresh((f) => ({ ...f, hist: h }))).catch(() => undefined);
  };

  if (histLoad.kind === 'loading') return <div className="deals lv-dp"><Loading what="the deal" /></div>;
  if (histLoad.kind === 'error') {
    return (
      <div className="deals lv-dp">
        <div className="dl-says">
          <div className="dl-err">The deal did not load. Nothing was changed.</div>
          <div className="dl-opts"><ActionButton reading type="button" className="dl-opt" onClick={reloadHist}>Try again</ActionButton></div>
        </div>
      </div>
    );
  }
  const hist = fresh.hist ?? histLoad.data;
  const heat = fresh.heat ?? (heatLoad.kind === 'ready' ? heatLoad.data : null);
  const { row, also } = heat ? rowAndAlso(heat, id) : { row: null, also: [] };
  const ctx: LiveContext | null = heat ? liveContextOf(heat, tenant, 'deals', open, refresh, kept) : null;
  // BUGS.md D97: Deals' line says when its account is put away (null), and then it is not named here either.
  const account = row ? row.accountName : hist.deal.organisationName;
  const accountId = row?.accountId ?? hist.deal.organisationId;
  const show = row?.eventId && heat ? heat.shows.find((s) => s.id === row.eventId) ?? null : null;
  const lines = historyLines(hist);
  const contact = row ? row.contact ?? null : head.contact ?? null;
  const events = all ? lines : lines.slice(0, HISTORY_FIRST);

  return (
    <div className="deals lv-dp">
      <ActionButton reading type="button" className="hx-link m lv-back" onClick={() => open('/deals')}>Deals</ActionButton>
      <div className="lv-rhead">
        <KeenBlock k={row?.keenness ?? null} />
        {/* "Lacerta Sports: One Trade"; a deal with no product is its own title, which already names the company. */}
        {/* The logo of the company the deal is filed on (pipeline RC8; U359, U371), beside the heading and not in it. */}
        <div className="co-with-logo lv-rtitle">
          {/* BUGS.md D97: a company put away is named nowhere, so it has no logo or initials here either, and the product
              stands alone. */}
          {hist.deal.organisationName !== null ? <Logo name={hist.deal.organisationName} logo={hist.deal.organisationLogo ?? null} /> : null}
          <h1>{(row?.product ?? hist.deal.product) ? (account !== null ? `${account}: ${row?.product ?? hist.deal.product}` : (row?.product ?? hist.deal.product)) : hist.deal.title}</h1>
        </div>
        <span className="lv-rmeta">
          {row ? <span>{holderWords(row)}</span> : <span>{head.mine ? 'Yours' : head.holderName ? possessive(head.holderName.split(' ')[0]!) : 'Nobody holds it yet'}</span>}
          {show ? <span>Met at {show.name}</span> : null}
          {/* BUGS.md D73 (Mike, by pop-up, 8 Oct about 12:37 CEST: "show them; the contact in the card's facts and the deal
              page's head"): their contact, as Add deal saved it, opening their page. */}
          {contact ? <span>Their contact: <ActionButton reading type="button" className="hx-link" onClick={() => open(`/people/${encodeURIComponent(contact.id)}`)}>{contact.name}</ActionButton></span> : null}
          {row?.lastHeardAt && ctx ? <span>Last heard {dayWords(row.lastHeardAt, ctx.dayStart)}</span> : null}
          {row ? null : <span>{headWords(head)}</span>}
          {/* BUGS.md D145: a declined or parked deal says why, as a rights deal's page does. */}
          {!row && (head.lifecycle === 'declined' || head.lifecycle === 'paused') && head.stateReason ? <span>“{head.stateReason}”</span> : null}
        </span>
      </div>
      {/* The Deal work lane's "This looks like the same deal" (W3, U260), at the top of the history page. */}
      {!row && heatLoad.kind !== 'loading' ? <DealCloseState store={tenant} id={id} open={open} changed={refresh} stamp={hist} /> : null}
      {ctx ? <CloseToasts ctx={ctx} /> : null}
      <SameDeal store={store.tenantId} dealId={id} onChanged={refresh} />
      {/* The Deal work lane's "Origins" (U260): how the deal started. Mounted as agreed with the P8 session, 5 Oct. */}
      <Origins store={store.tenantId} dealId={id} open={open} stamp={hist.events.length} />

      {row && ctx ? <DealTop row={row} accountName={account} ctx={ctx} /> : heatLoad.kind === 'loading' ? <Loading what="where it stands" /> : null}

      <div className="lv-rgrid">
        <section className="lv-panel" aria-label="Everything on this deal">
          <div className="lv-ph"><h2>Everything on this deal</h2><span className="hx-meta">the newest first</span></div>
          {lines.length === 0
            ? <p className="lv-meta">Nothing is filed under it yet, and nobody has changed it.</p>
            : <ul className="lv-tline">{events.map((e) => e.kind === 'act'
              ? <ActRow key={`a-${e.act.actionId}`} a={e.act} store={tenant} dealId={id} now={heat?.now ?? new Date().toISOString()} dayStart={ctx?.dayStart ?? null} refresh={refresh} />
              : <EventRow key={e.kind === 'message' ? `m-${e.message.signalId}` : `c-${e.change.changeId}`} e={e} store={tenant} now={heat?.now ?? new Date().toISOString()} dayStart={ctx?.dayStart ?? null} mayEdit={Boolean(row && ctx?.mayEdit)} open={open} refresh={refresh} />)}</ul>}
          {!all && lines.length > HISTORY_FIRST
            ? <ActionButton reading type="button" className="hx-link" onClick={() => setAll(true)}>Show {lines.length - HISTORY_FIRST} older</ActionButton>
            : null}
          {all && hist.more ? <p className="lv-meta">The newest 500 messages and changes are listed.</p> : null}
        </section>
        {/* BUGS.md D97: an account put away is named nowhere and has no record to open, so there is no "Also at" it. */}
        {also.length > 0 && ctx && account !== null
          ? (
            <aside>
              <section className="lv-panel" aria-label={`Also at ${account}`}>
                <div className="lv-ph">
                  <h2>Also at {account}</h2>
                  <ActionButton reading type="button" className="hx-link" onClick={() => open(`/companies/${encodeURIComponent(accountId)}`)}>Open the company</ActionButton>
                </div>
                {also.map((r) => (
                  <div className="lv-pv" key={r.id} data-stage={stageTone(r.live?.stageCode)}>
                    <KeenBlock k={r.keenness} small />
                    <ActionButton reading type="button" className="lv-pvn" onClick={() => open(`/deals/${encodeURIComponent(r.id)}`)}>{r.product ?? r.title}</ActionButton>
                    <span className="x">
                      <span className="st"><span className="stw">{r.live?.stageName ?? 'No stage'}</span> <Track stages={ctx.stages} code={r.live?.stageCode ?? null} /></span>
                      <Status s={rowStep(r)} />
                    </span>
                  </div>
                ))}
              </section>
            </aside>
          )
          : null}
      </div>
    </div>
  );
}

/** A line of the history: a message, a change, or an act on the deal itself (D142, D146). */
export type HistoryLine = HistoryEvent | { kind: 'act'; at: string; act: DealAct };

/** The messages, changes and acts in one timeline, the newest first. */
export function historyLines(h: Pick<DealHistory, 'events' | 'acts'>): HistoryLine[] {
  const acts: HistoryLine[] = (h.acts ?? []).map((a) => ({ kind: 'act', at: a.at, act: a }));
  return acts.length === 0 ? h.events : [...h.events, ...acts].sort((a, b) => b.at.localeCompare(a.at));
}

/**
 * An act's line: who did it, to whom and when, "Undone" once taken back, and its Undo for the person who did it while its
 * week lasts (Mike, FT2, 6 Oct: "Undo until <date>"). Not tied to the card's edit right: a removed deal is on no list, and
 * its page is where its remove is undone. The store keeps each undo's own rules and says why when it refuses.
 */
function ActRow({ a, store, dealId, now, dayStart, refresh }: { a: DealAct; store: string; dealId: string; now: string; dayStart: string | null; refresh: () => void }) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const canUndo = actUndoable(a, now) && !done;
  const undo = () => {
    setBusy(true);
    (a.kind === 'won' || a.kind === 'lost' ? closeApi.undo(store, dealId, a.kind) : dealsApi.undoAct(store, dealId, a.actionId))
      .then(() => { setBusy(false); setDone(true); refresh(); })
      .catch((err: unknown) => { setBusy(false); setSaid(err instanceof DealsError ? `It was not undone: ${err.message}.` : 'It was not undone. Try again.'); });
  };
  return (
    <li>
      <span className="d">{dayWords(a.at, dayStart)}</span>
      <span className="h">{actWords(a)}{a.undoneAt || done ? <span className="lv-undone">Undone</span> : null}</span>
      {canUndo || said
        ? (
          <span className="b">
            {canUndo ? <ActionButton type="button" className="hx-link" disabled={busy} onClick={undo}>{actUndoWords(a)}</ActionButton> : null}
            {said ? <span className="lv-said" role="status"> {said}</span> : null}
          </span>
        )
        : null}
    </li>
  );
}

/** One line of the history: a message, or a change with who made it, from what, and an Undo on Cobrain's own. */
function EventRow({ e, store, now, dayStart, mayEdit, open, refresh }: { e: HistoryEvent; store: string; now: string; dayStart: string | null; mayEdit: boolean; open: (p: string) => void; refresh: () => void }) {
  if (e.kind === 'message') {
    const m = e.message;
    const path = opensPath(m.signalId, m.opens, store);
    return (
      <li className={[m.today ? 'new' : '', m.fromMe ? 'you' : ''].filter(Boolean).join(' ') || undefined}>
        <span className="d">{dayWords(m.at, dayStart)}</span>
        <span className="h"><span className="chip">{kindChip(m.kind)}</span>{historySender(m)}</span>
        <span className="b">{path ? <ActionButton reading type="button" className="hx-link lv-tlink" onClick={() => open(path)}>{subjectWords(m) ?? 'Open it'}</ActionButton> : subjectWords(m) ?? ''}</span>
      </li>
    );
  }
  return <ChangeRow c={e.change} store={store} now={now} dayStart={dayStart} mayEdit={mayEdit} refresh={refresh} />;
}

/** A change's line; Undo and "Change it" only on an open deal the reader may edit (U152, U232). */
function ChangeRow({ c, store, now, dayStart, mayEdit, refresh }: { c: HistoryChange; store: string; now: string; dayStart: string | null; mayEdit: boolean; refresh: () => void }) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const w = changeWords(c);
  const canUndo = mayEdit && historyUndoable(c, now) && !said;
  const canChange = mayEdit && c.source === 'inferred' && c.field === 'keenness' && c.undoes === null && !c.undoneBy && !said;
  const undo = () => {
    setBusy(true);
    dealsApi.undoChange(store, c.changeId)
      .then(() => { setBusy(false); setSaid(c.source === 'seed' ? 'Undone. The sheet will keep your stage.' : 'Undone. Cobrain will not move it again from that message.'); refresh(); })
      .catch((err: unknown) => { setBusy(false); setSaid(err instanceof DealsError ? `It was not undone: ${err.message}.` : 'It was not undone. Try again.'); });
  };
  const toKeenness = () => {
    const box = document.getElementById('dp-keenness');
    box?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    box?.querySelector<HTMLButtonElement>('button[aria-pressed="true"], button')?.focus();
  };
  return (
    <li className={c.source === 'inferred' ? 'cb' : undefined}>
      <span className="d">{dayWords(c.at, dayStart)}</span>
      <span className="h">{w.head}{c.undoneBy ? <span className="lv-undone">Undone</span> : null}</span>
      {w.body || canUndo || canChange || said
        ? (
          <span className="b">
            {w.body}
            {canUndo ? <> <ActionButton type="button" className="hx-link" disabled={busy} onClick={undo}>Undo</ActionButton></> : null}
            {canChange ? <> <ActionButton type="button" className="hx-link" onClick={toKeenness}>Change it</ActionButton></> : null}
            {said ? <span className="lv-said" role="status"> {said}</span> : null}
          </span>
        )
        : null}
    </li>
  );
}
